import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import { InvTextField } from '../../components/InvField';
import { getTransfers, getStores } from '../../api/stockLedger';
import { toInventoryPage } from '../../utils/masterEntity';
import { storeDisplayName } from '../../utils/storeEntity';
import { formatDate, formatQty } from '../../utils';
import { TRANSFER_STATUSES } from '../../constants/stockTransfer';
import useMasterList from '../../hooks/useMasterList';
import useAssignedStoreOptions from '../../hooks/useAssignedStoreOptions';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const PAGE_SIZE = 25;

function normalizeStoreList(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

/** The list carries store names, so fall back to the id only for legacy rows. */
function storeCell(name, code, id) {
  if (name) return code ? `${name} (${code})` : name;
  return id || '—';
}

export default function TransferList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { can } = useInvRole();
  const list = useMasterList({ pageSize: PAGE_SIZE });
  const {
    items, setItems, totalCount, setTotalCount,
    loading, setLoading, error, setError,
    page, setPage, searchInput, setSearchInput, search,
    statusFilter, setStatusFilter, pageSize,
  } = list;

  const [stores, setStores] = useState([]);
  const [fromStore, setFromStore] = useState('');
  const [toStore, setToStore] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [transferNo, setTransferNo] = useState('');

  const { storeLocked, activeStoreId, isStoreScoped, storeOptions } = useAssignedStoreOptions(stores);
  const highlightId = new URLSearchParams(location.search).get('highlight');

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores()
      .then(d => setStores(normalizeStoreList(d)))
      .catch(() => setStores([]));
  }, []);

  // A user assigned to exactly one store only ever sends stock out of that store.
  const lockedFromStore = storeLocked && activeStoreId ? activeStoreId : '';
  const effectiveFromStore = lockedFromStore || fromStore;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // API: GET /inventory/stock/transfers/
      const raw = await getTransfers({
        from_store_id: effectiveFromStore || undefined,
        to_store_id: toStore || undefined,
        status: statusFilter || undefined,
        transaction_number: transferNo || undefined,
        search: search || undefined,
        from_date: dateFrom || undefined,
        to_date: dateTo || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      const { items: fetched, total } = toInventoryPage(raw, { page, pageSize });
      setItems(fetched);
      setTotalCount(total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [
    effectiveFromStore, toStore, statusFilter, transferNo, search, dateFrom, dateTo,
    page, pageSize, setItems, setTotalCount, setLoading, setError,
  ]);

  useEffect(() => { load(); }, [load]);

  // Every filter is server-side, so a change has to restart at the first page or
  // the user lands on an offset the new result set may not reach.
  function applyFilter(setter) {
    return (value) => { setter(value); setPage(0); };
  }

  const hasActiveFilters = Boolean(
    fromStore || toStore || dateFrom || dateTo || transferNo || statusFilter || searchInput,
  );

  function resetFilters() {
    setFromStore('');
    setToStore('');
    setDateFrom('');
    setDateTo('');
    setTransferNo('');
    list.resetFilters();
  }

  const allStoreOptions = useMemo(() => [
    { value: '', label: 'All' },
    ...stores.map(s => ({ value: s.store_id || s.id, label: storeDisplayName(s) })),
  ], [stores]);

  const lockedFromStoreLabel = useMemo(() => {
    const store = stores.find(s => (s.store_id || s.id) === lockedFromStore);
    return store ? storeDisplayName(store) : lockedFromStore;
  }, [stores, lockedFromStore]);

  const openTransfer = useCallback(
    (id) => history.push(`${url}/${id}`),
    [history, url],
  );

  const columns = useMemo(() => [
    {
      field: 'transaction_number',
      headerName: 'Transfer #',
      width: 220,
      renderCell: p => (
        <Box
          component="span"
          title={p.value || undefined}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: p.row.transaction_id === highlightId ? 700 : 400,
            color: p.row.transaction_id === highlightId ? IPD_COLORS.primary : 'inherit',
          }}
        >
          {p.value || '—'}
        </Box>
      ),
    },
    {
      field: 'from_store_name',
      headerName: 'From Store',
      width: 200,
      valueGetter: p => storeCell(p.row.from_store_name, p.row.from_store_code, p.row.from_store_id),
    },
    {
      field: 'to_store_name',
      headerName: 'To Store',
      width: 200,
      valueGetter: p => storeCell(p.row.to_store_name, p.row.to_store_code, p.row.to_store_id),
    },
    {
      field: 'transaction_date',
      headerName: 'Transfer Date',
      width: 130,
      valueFormatter: p => formatDate(p.value),
    },
    {
      field: 'item_count',
      headerName: 'Items',
      width: 90,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'total_quantity',
      headerName: 'Total Qty',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: p => formatQty(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: p => (p.value ? <StatusChip status={p.value} minWidth={110} /> : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      renderCell: p => (
        <Tooltip title="View">
          <IconButton
            size="small"
            onClick={() => openTransfer(p.row.transaction_id)}
            sx={{ color: IPD_COLORS.primary }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [highlightId, openTransfer]);

  const createButton = can(INV_ACTIONS.TRANSFER_CREATE)
    ? (
      <InvButton
        startIcon={<AddIcon />}
        onClick={() => history.push(`${url.replace('/transfers', '')}/transfer/new`)}
      >
        Create Transfer
      </InvButton>
    )
    : null;

  return (
    <Box>
      <PageHeader
        eyebrow="Inventory Management"
        title="Stock Transfers"
        description={`${totalCount} transfer(s)${isStoreScoped ? ' · your store only' : ''}`}
        actions={createButton}
      />
      <FilterToolbar>
        {lockedFromStore
          ? <FilterToolbar.StaticValue label="From Store" value={lockedFromStoreLabel} width="wide" />
          : (
            <FilterToolbar.Filter
              label="From Store"
              width="wide"
              value={fromStore}
              onChange={e => applyFilter(setFromStore)(e.target.value)}
              options={isStoreScoped ? [{ value: '', label: 'All' }, ...storeOptions] : allStoreOptions}
            />
          )}
        <FilterToolbar.Filter
          label="To Store"
          width="wide"
          value={toStore}
          onChange={e => applyFilter(setToStore)(e.target.value)}
          options={allStoreOptions}
        />
        <FilterToolbar.DateFilter
          label="From Date"
          value={dateFrom}
          onChange={e => applyFilter(setDateFrom)(e.target.value)}
        />
        <FilterToolbar.DateFilter
          label="To Date"
          value={dateTo}
          onChange={e => applyFilter(setDateTo)(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={e => applyFilter(setStatusFilter)(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...TRANSFER_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') })),
          ]}
        />
        {/* Slot rather than .Filter — the toolbar's filter primitive is a select. */}
        <FilterToolbar.Slot width={150}>
          <InvTextField
            label="Transfer No."
            value={transferNo}
            onChange={e => applyFilter(setTransferNo)(e.target.value)}
            fullWidth
            sx={{ '& .MuiFormHelperText-root': { display: 'none' } }}
          />
        </FilterToolbar.Slot>
        <FilterToolbar.Search
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Transfer number or store name"
        />
        <FilterToolbar.Reset onClick={resetFilters} disabled={!hasActiveFilters} />
      </FilterToolbar>
      <InvDataGrid
        rows={items}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={r => r.transaction_id}
        emptyMessage="No transfers found."
        emptyDescription="Stock transfers move items from one store to another. Create one to get started."
        emptyAction={createButton}
        paginationMode="server"
        rowCount={totalCount}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => setPage(model.page)}
        pageSizeOptions={[pageSize]}
      />
    </Box>
  );
}
